// Step 1 of booking. Services live in settings/bookingServices so the admin
// dashboard can manage the catalogue without a frontend release.

import React, { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { Link } from "react-router-dom";
import { db } from "../../firebase";
import {
	BOOKING_CONTACT_EMAIL,
	BOOKING_SERVICES_SETTINGS_ID,
	defaultBookingServices,
	normaliseBookingServices,
	packagePriceLabel,
	serviceOptionForBooking,
	servicePriceLabel,
} from "../../data/bookingServices";

const MODES = [
	{ id: "in-person", label: "In Person", description: "Come in for a face-to-face session.", icon: "🏢" },
	{ id: "virtual", label: "Virtual", description: "Join from anywhere via video call.", icon: "💻" },
];

const ServiceSelector = ({ onSelect }) => {
	const [selectedService, setSelectedService] = useState(null);
	const [selectedPackage, setSelectedPackage] = useState(null);
	const [catalogue, setCatalogue] = useState(null);
	const [legacyPrices, setLegacyPrices] = useState({});

	useEffect(() => {
		const unsubscribeCatalogue = onSnapshot(
			doc(db, "settings", BOOKING_SERVICES_SETTINGS_ID),
			(snapshot) => setCatalogue(snapshot.exists() ? normaliseBookingServices(snapshot.data()?.services) : null),
			(err) => {
				console.error("Failed to load booking services:", err);
				setCatalogue(null);
			}
		);
		const unsubscribeLegacyPricing = onSnapshot(
			doc(db, "settings", "servicePricing"),
			(snapshot) => setLegacyPrices(snapshot.exists() ? snapshot.data() || {} : {}),
			(err) => console.error("Failed to load legacy service pricing:", err)
		);
		return () => {
			unsubscribeCatalogue();
			unsubscribeLegacyPricing();
		};
	}, []);

	const services = useMemo(
		() => (catalogue || defaultBookingServices(legacyPrices)).filter((service) => service.active),
		[catalogue, legacyPrices]
	);

	const chooseService = (service) => {
		setSelectedService(service);
		setSelectedPackage(service.packages.length === 1 ? service.packages[0] : null);
	};

	const chooseMode = (mode) => {
		onSelect({ ...serviceOptionForBooking(selectedService, selectedPackage), mode: mode.id });
	};

	const returnToServices = () => {
		setSelectedService(null);
		setSelectedPackage(null);
	};

	const returnToPackages = () => {
		if (selectedService?.packages.length > 1) setSelectedPackage(null);
		else returnToServices();
	};

	if (selectedService && !selectedPackage) {
		return (
			<div className="service-selector">
				<div className="booking-step-heading">
					<div className="step-eyebrow">Step 1 of 3</div>
					<h2 className="step-title">Choose your counselling option</h2>
					<p className="step-subtitle"><strong>{selectedService.title}</strong> &middot; {selectedService.duration} min appointments</p>
				</div>

				<div className="service-grid">
					{selectedService.packages.map((pkg) => (
						<button key={pkg.id} type="button" className="service-card" onClick={() => setSelectedPackage(pkg)}>
							<div className="service-card-title">{pkg.label}</div>
							<div className="service-card-tagline">
								{pkg.priceType === "on-request" ? "Tell us what you need and we will tailor a quote." : `${selectedService.duration} min appointment`}
							</div>
							<div className="service-card-meta">
								<span className="service-card-price">{packagePriceLabel(pkg)}</span>
								<span className="service-card-arrow">&rarr;</span>
							</div>
						</button>
					))}
				</div>

				<div className="step-actions">
					<button type="button" className="btn btn-ghost" onClick={returnToServices}>&larr; Back</button>
				</div>
			</div>
		);
	}

	if (selectedService && selectedPackage) {
		const availableModes = MODES.filter((mode) => selectedService.modes.includes(mode.id));
		return (
			<div className="service-selector">
				<div className="booking-step-heading">
					<div className="step-eyebrow">Step 1 of 3</div>
					<h2 className="step-title">How would you like to meet?</h2>
					<p className="step-subtitle"><strong>{selectedService.title}</strong> &middot; {selectedPackage.label} &middot; {packagePriceLabel(selectedPackage)}</p>
				</div>

				<div className="service-grid">
					{availableModes.map((mode) => (
						<button key={mode.id} type="button" className="service-card" onClick={() => chooseMode(mode)}>
							<div className="service-card-icon">{mode.icon}</div>
							<div className="service-card-title">{mode.label}</div>
							<div className="service-card-tagline">{mode.description}</div>
							<div className="service-card-meta"><span className="service-card-arrow">&rarr;</span></div>
						</button>
					))}
				</div>

				<div className="step-actions">
					<button type="button" className="btn btn-ghost" onClick={returnToPackages}>&larr; Back</button>
				</div>
			</div>
		);
	}

	return (
		<div className="service-selector">
			<div className="booking-step-heading">
				<div className="step-eyebrow">Step 1 of 3</div>
				<h2 className="step-title">What would you like to book?</h2>
				<p className="step-subtitle">Choose a service to see available dates and times.</p>
			</div>

			{services.length ? (
				<div className="service-grid">
					{services.map((service) => (
						<button key={service.id} type="button" className="service-card" onClick={() => chooseService(service)}>
							<div className="service-card-title">{service.title}</div>
							<div className="service-card-tagline">{service.tagline}</div>
							<div className="service-card-meta">
								<span>{service.duration} min</span>
								<span className="service-card-price">{servicePriceLabel(service)}</span>
								<span className="service-card-arrow">&rarr;</span>
							</div>
						</button>
					))}
				</div>
			) : (
				<p className="step-subtitle">Bookings are temporarily unavailable. Please check back soon.</p>
			)}

			<p className="booking-help-callout">
				Questions before you book? <a href={`mailto:${BOOKING_CONTACT_EMAIL}?subject=${encodeURIComponent("Booking question")}`}>Email {BOOKING_CONTACT_EMAIL}</a>.
			</p>

			<div className="events-promo">
				<div className="events-promo-copy">
					<span className="events-promo-kicker">Jabuzwane Events</span>
					<h3>Make your next event unforgettable.</h3>
					<p>Discover upcoming experiences and secure your seat while tickets are available.</p>
				</div>
				<Link className="events-promo-button" to="/events">Buy available event tickets <span aria-hidden="true">&rarr;</span></Link>
			</div>
		</div>
	);
};

export default ServiceSelector;
